import { useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuthStore } from "../store/authStore";

interface AuditLogEntry {
	operation: "CREATE" | "READ" | "UPDATE" | "DELETE";
	table_name: string;
	record_id?: string;
	old_values?: Record<string, any>;
	new_values?: Record<string, any>;
}

/**
 * Hook per loggare operazioni admin nel database.
 * Tutti i log vengono salvati in admin_audit_log table con protezione RLS.
 *
 * @example
 * const { logAction } = useAuditLog();
 *
 * const handleDeleteProduct = async (productId: string) => {
 *   await supabase.from('products').delete().eq('id', productId);
 *   await logAction({
 *     operation: 'DELETE',
 *     table_name: 'products',
 *     record_id: productId
 *   });
 * };
 */
export const useAuditLog = () => {
	const { user } = useAuthStore();

	const logAction = useCallback(
		async (entry: AuditLogEntry): Promise<boolean> => {
			if (!user) {
				console.error("Cannot log action: User not authenticated");
				return false;
			}

			try {
				// Chiama la funzione Postgres per loggare l'operazione
				const { error } = await supabase.rpc("log_admin_operation", {
					admin_id: user.id,
					operation: entry.operation,
					table_name: entry.table_name,
					record_id: entry.record_id || null,
					old_vals: entry.old_values ? JSON.stringify(entry.old_values) : null,
					new_vals: entry.new_values ? JSON.stringify(entry.new_values) : null,
				});

				if (error) {
					console.error("Audit log error:", error);
					return false;
				}

				return true;
			} catch (error) {
				console.error("Failed to log action:", error);
				return false;
			}
		},
		[user],
	);

	const logCreateAction = useCallback(
		(tableName: string, recordId: string, values: Record<string, any>) => {
			return logAction({
				operation: "CREATE",
				table_name: tableName,
				record_id: recordId,
				new_values: values,
			});
		},
		[logAction],
	);

	const logUpdateAction = useCallback(
		(
			tableName: string,
			recordId: string,
			oldValues: Record<string, any>,
			newValues: Record<string, any>,
		) => {
			return logAction({
				operation: "UPDATE",
				table_name: tableName,
				record_id: recordId,
				old_values: oldValues,
				new_values: newValues,
			});
		},
		[logAction],
	);

	const logDeleteAction = useCallback(
		(tableName: string, recordId: string, oldValues?: Record<string, any>) => {
			return logAction({
				operation: "DELETE",
				table_name: tableName,
				record_id: recordId,
				old_values: oldValues,
			});
		},
		[logAction],
	);

	return {
		logAction,
		logCreateAction,
		logUpdateAction,
		logDeleteAction,
	};
};
